import { findEnumName } from "@/lib/enum-utils";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Button } from "@/components/ui/button";
import { PortalPageHeader } from "@/components/portal-page-header";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { OrganizationFormStatus, type OrganizationFormListItemDTO } from "@/orval/model";
import {
  getOrganizationFormsGetFormsQueryKey,
  getOrganizationFormsGetFormsQueryOptions,
  organizationFormsGetForms,
} from "@/orval/organization-forms";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "lucide-react";

const columns: ColumnDef<OrganizationFormListItemDTO>[] = [
  { header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />, accessorKey: "name" },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    accessorKey: "status",
    cell: ({ row }) => findEnumName(OrganizationFormStatus, row.original.status ?? OrganizationFormStatus.Draft),
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Fields" />,
    accessorKey: "fieldCount",
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.fieldCount}</span>,
  },
];

function FormsPage() {
  const { organizationId } = Route.useParams();
  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6 [&_thead_tr]:top-0 [&_table]:h-auto">
      <PortalPageHeader
        className="mb-8"
        title="Forms"
        subtitle="Create and manage forms for organization families."
        actions={
          <Button asChild>
            <Link to="/organizations/$organizationId/admin/forms/new" params={{ organizationId }}>
              <PlusIcon className="mr-2 size-4" />
              New Form
            </Link>
          </Button>
        }
      />
      <DataTable
        contentLayout
        columns={columns}
        queryFn={(params, signal) => organizationFormsGetForms(organizationId, params, signal)}
        queryKeyFn={(params) => getOrganizationFormsGetFormsQueryKey(organizationId, params)}
        rowLink={(row) => ({
          to: "/organizations/$organizationId/admin/forms/$formId",
          params: { organizationId, formId: row.id },
          search: { tab: "layout" },
        })}
        getRowIdFn={(row) => row.id.toString()}
      />
    </div>
  );
}

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/forms")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) =>
    ensureRouteQueryData(
      loaderContext,
      getOrganizationFormsGetFormsQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions(searchStringSearchSchema.parse(location.search)),
      ),
    ),
  component: FormsPage,
});
